
import { Star, MapPin, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCallback, useRef, memo, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useFavorites } from "@/hooks/useFavorites";
import { ServiceImageLoader } from "@/components/ServiceImageLoader";
import { isLowDataMode } from "@/utils/networkMonitor";
import { 
  isDevEnvironment,
  isPreviewEnvironment,
  logImageDetails
} from "@/utils/imageTransformationUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { DebugTooltip } from "@/components/ui/debug-tooltip";
import { analyzeImage } from "@/utils/imageAnalysis";

interface AdaptiveServiceCardProps {
  id: number;
  title: string;
  subcategory_details: {
    label: string | null;
    parent: {
      label: string | null;
    } | null;
  } | null;
  price: number | null;
  rating: number | null;
  image: string;
  contact: {
    phone: string | null;
    email: string | null;
    location: string | null;
  };
  region: string;
  cardType?: 'portrait' | 'landscape' | 'square' | 'auto';
  aspectRatio?: number;
}

export const AdaptiveServiceCard = memo(({ 
  id, 
  title, 
  subcategory_details, 
  price, 
  rating, 
  image,
  contact,
  region = "nairobi",
  cardType = 'auto',
  aspectRatio
}: AdaptiveServiceCardProps) => {
  const navigate = useNavigate();
  const { isFavorite, addFavorite, removeFavorite, isSyncing, isAuthenticated } = useFavorites();
  // Safely extract category information to prevent React #31 errors
  const categoryLabel = typeof subcategory_details?.parent?.label === 'string' 
    ? subcategory_details.parent.label 
    : 'Uncategorized';
  const subcategoryLabel = typeof subcategory_details?.label === 'string' 
    ? subcategory_details.label 
    : null;
  const displayCategory = subcategoryLabel ? `${categoryLabel} - ${subcategoryLabel}` : categoryLabel;
  const isLowDataRef = useRef(isLowDataMode());
  const isMobile = useIsMobile();
  const isPriority = id <= 3;
  
  const [finalImageUrl, setFinalImageUrl] = useState<string>(image || 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d');
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [transformId] = useState<string>(`transform-${id}-${Math.random().toString(36).substring(2, 9)}`);
  const [cardAspectRatio, setCardAspectRatio] = useState<number>(aspectRatio || 4/3);
  const [detectedCardType, setDetectedCardType] = useState<'portrait' | 'landscape' | 'square'>('square');

  // Analyze image when it loads to determine optimal card type
  const handleImageAnalysis = useCallback((img: HTMLImageElement) => {
    if (cardType === 'auto') {
      const analysis = analyzeImage(img);
      setDetectedCardType(analysis.cardType);
      setCardAspectRatio(analysis.recommendedCardAspectRatio);
    }
  }, [cardType]);

  useEffect(() => {
    if ((isDevEnvironment() || isPreviewEnvironment()) && image) {
      setDebugInfo(`Original URL: ${image}`);
      logImageDetails(transformId, image, null);
    }
  }, [id, image, transformId]);

  // Use provided cardType or detected type
  const effectiveCardType = cardType === 'auto' ? detectedCardType : cardType;
  
  // Determine grid span class based on card type
  const getGridSpanClass = () => {
    switch (effectiveCardType) {
      case 'portrait':
        return 'col-span-1 row-span-2'; // Take up 2 rows
      case 'landscape':
        return 'col-span-2 row-span-1'; // Take up 2 columns on larger screens
      case 'square':
      default:
        return 'col-span-1 row-span-1';
    }
  };

  // Adjust minimum height based on card type
  const getMinHeight = () => {
    switch (effectiveCardType) {
      case 'portrait':
        return '480px';
      case 'landscape':
        return '320px';
      case 'square':
      default:
        return '380px';
    }
  };
  
  const handleCardClick = useCallback(() => {
    navigate(`/service/${id}`);
  }, [id, navigate]);

  const handleFavoriteClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card navigation
    
    if (!isAuthenticated) return;
    
    if (isFavorite(id)) {
      await removeFavorite(id);
    } else {
      await addFavorite(id);
    }
  }, [id, isFavorite, addFavorite, removeFavorite, isAuthenticated]);

  return (
    <Card 
      onClick={handleCardClick}
      data-testid="service-card"
      className={`group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer h-full flex flex-col ${getGridSpanClass()}`}
      style={{
        contain: 'content',
        minHeight: getMinHeight(),
      }}
    >
      <div className="relative bg-gray-100 flex-shrink-0" style={{ 
        height: '0',
        paddingBottom: `${(1 / cardAspectRatio) * 100}%`,
        position: 'relative',
        contain: 'strict',
        aspectRatio: cardAspectRatio,
      }}>
        {/* Favorite Button Overlay */}
        {isAuthenticated && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleFavoriteClick}
            disabled={isSyncing}
            className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-white/80 hover:bg-white/90 shadow-sm"
            aria-label={isFavorite(id) ? "Remove from favorites" : "Add to favorites"}
          >
            <Heart 
              className={`h-4 w-4 ${isFavorite(id) ? "fill-red-500 text-red-500" : "text-gray-600"}`} 
            />
          </Button>
        )}
        {(isDevEnvironment() || isPreviewEnvironment()) ? (
          <DebugTooltip
            content={
              <div>
                <p className="font-mono break-all">
                  <span className="text-blue-300">Image URL:</span> {image}
                </p>
                <p className="text-green-300">Card Type: {effectiveCardType}</p>
                <p className="text-yellow-300">Aspect Ratio: {cardAspectRatio.toFixed(2)}</p>
                {debugInfo && (
                  <div className="mt-1 pt-1 border-t border-gray-600">
                    <pre className="whitespace-pre-wrap break-all">{debugInfo}</pre>
                  </div>
                )}
              </div>
            }
          >
            <ServiceImageLoader
              src={finalImageUrl}
              alt={title || "Service image"}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              aspectRatio={cardAspectRatio}
              priority={isPriority}
              showPlaceholder={!isPriority}
              onLoad={() => {
                const img = new Image();
                img.onload = () => handleImageAnalysis(img);
                img.src = finalImageUrl;
              }}
            />
          </DebugTooltip>
        ) : (
          <ServiceImageLoader
            src={finalImageUrl}
            alt={title || "Service image"}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            aspectRatio={cardAspectRatio}
            priority={isPriority}
            showPlaceholder={!isPriority}
            onLoad={() => {
              const img = new Image();
              img.onload = () => handleImageAnalysis(img);
              img.src = finalImageUrl;
            }}
          />
        )}
      </div>
      <CardContent className="p-4 flex-1 flex flex-col" style={{ minHeight: effectiveCardType === 'portrait' ? '200px' : '160px' }}>
        {!isLowDataRef.current && (
          <div 
            className="inline-block px-2 py-0.5 text-xs font-medium text-gray-700 rounded-full mb-1 max-w-full transition-colors duration-300 hover:opacity-90"
            style={{ backgroundColor: 'var(--brand-color-5, #F5FBEF)' }}
          >
            <span className="truncate text-gray-700" style={{ display: 'block' }}>{displayCategory}</span>
          </div>
        )}
        
        <h3 
          className="text-sm font-semibold mb-2" 
          style={{ 
            minHeight: effectiveCardType === 'portrait' ? '3rem' : '2.5rem', 
            display: '-webkit-box', 
            WebkitLineClamp: effectiveCardType === 'portrait' ? 3 : 2, 
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {title || 'Untitled Service'}
        </h3>
        <div className="flex items-center justify-between mt-auto" style={{ minHeight: '1.75rem' }}>
          {price !== null && (
            <div className="text-lg font-bold text-primary truncate max-w-[60%]">
              {region === "nairobi" ? "Ksh" : "USD"} {price}
            </div>
          )}
          {rating !== null && (
            <div className="flex items-center gap-0.5 ml-auto">
              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
              <span className="text-xs font-medium">{rating.toFixed(1)}</span>
            </div>
          )}
        </div>
        
        <div className="space-y-1 text-xs text-gray-600 mt-2" style={{ minHeight: effectiveCardType === 'portrait' ? '4rem' : '3rem' }}>
          {contact.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{contact.location}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

AdaptiveServiceCard.displayName = "AdaptiveServiceCard";
